import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormLabel,
  Grid,
  IconButton,
  Theme,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Cancel, Create } from '@material-ui/icons';
import { red } from '@material-ui/core/colors';

import Input from '../Input';
import { useAxios } from '../../utils/hooks';
import Snackbar, { AlertComponentProps } from '../Snackbar';
import { SignatureForm } from '../../models/signature-form';
import { Signature as SignatureModel } from '../../models/signature';
import getValidationErrors from '../../utils/getValidationErrors';

interface SignatureProps {
  formAnswers: { signatures: SignatureForm[] };
  onSignature: (signatures: SignatureForm[]) => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  formTitles: {
    fontWeight: 'bolder',
  },
  formControl: {
    margin: theme.spacing(2, 0),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  container: {
    display: 'flex',
  },
  signature: {
    width: '50%',
    padding: theme.spacing(2),
  },
}));

const Signature: React.FC<SignatureProps> = ({ formAnswers, onSignature }) => {
  const classes = useStyles();
  const api = useAxios();
  const [signatures, setSignatures] = useState(formAnswers.signatures || []);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [alert, setAlert] = useState<
    AlertComponentProps & { isActive: boolean }
  >({
    isActive: false,
    type: 'info',
    message: 'none',
  });

  const resetAlert = () => {
    setAlert({
      isActive: false,
      type: 'info',
      message: 'none',
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmitSignature = useCallback(async () => {
    try {
      resetAlert();

      const schema = Yup.object().shape({
        password: Yup.string()
          .required('Informe uma senha válida')
          .min(4, 'Senha deve no mínimo conter 4 dígitos')
          .max(4, 'Senha deve no máximo conter 4 dígitos'),
      });

      await schema.validate({ password }, { abortEarly: false });

      const passwordToSend = Number(password);

      await api.current?.post(`/users/signature/validate`, {
        password: passwordToSend,
      });

      const userSignature = await api.current?.get<SignatureModel>(
        `/users/signature`,
      );

      if (userSignature && userSignature.data) {
        const signaturesToSave: SignatureForm[] = [
          ...signatures,
          {
            name: userSignature?.data.name || '--',
            number: userSignature?.data.number || '--',
            url: userSignature?.data.file.path || '--',
            register: userSignature?.data.register || '--',
          },
        ];

        setSignatures(signaturesToSave);
        onSignature(signaturesToSave);
      }

      setAlert({
        isActive: true,
        type: 'success',
        message: 'Assinatura aplicada com sucesso.',
      });

      handleClose();
    } catch (error: any) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error);
        setPasswordError(errors.password);
      } else if (error?.response?.status === 401) {
        setAlert({
          isActive: true,
          type: 'warning',
          message: error.response.data.message,
        });
      } else {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao aplicar assinatura.',
        });
      }
    }
  }, [api, password, signatures, onSignature]);

  const handleCancelSignature = useCallback(
    async (number: string) => {
      try {
        setLoading(true);
        resetAlert();

        const userSignature = await api.current?.get<SignatureModel>(
          `/users/signature`,
        );

        if (userSignature?.data?.number !== number) {
          setAlert({
            isActive: true,
            type: 'error',
            message: 'Remova somente a sua assinatura.',
          });

          return;
        }

        const currentSignatures = signatures.filter(
          (signature) => signature.number !== number,
        );

        setSignatures(currentSignatures);
        onSignature(currentSignatures);

        setAlert({
          isActive: true,
          type: 'success',
          message: 'Assinatura removida com sucesso.',
        });

        handleClose();
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'warning',
          message: 'Erro ao remover assinatura.',
        });
      } finally {
        setLoading(false);
      }
    },
    [signatures, api, onSignature],
  );

  const renderDialog = (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      fullWidth
    >
      <DialogTitle id="form-dialog-title">Assinar Ficha</DialogTitle>

      <Grid item xs={12}>
        <Divider />
      </Grid>

      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} />

          <Grid item xs={12}>
            <FormLabel className={classes.formTitles}>
              Digite sua senha de 4 dígitos
            </FormLabel>
          </Grid>
          <Grid item xs={12}>
            <span>*caso não tenha, cadastre no seu perfil</span>
          </Grid>
          <Grid item xs={12}>
            <Input
              id="password"
              type="password"
              label="Senha"
              manualError={passwordError}
              name="password"
              onChange={(e) => setPassword(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={() => handleSubmitSignature()}
        >
          {loading ? 'Enviando...' : 'Enviar'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      {renderDialog}

      <Typography align="right">
        <Button
          startIcon={<Create />}
          variant="outlined"
          color="primary"
          onClick={() => setOpen(true)}
        >
          Assinar
        </Button>
      </Typography>

      <div className={classes.container}>
        {signatures.map((signature) => (
          <div className={classes.signature}>
            <Typography variant="h3" component="h3">
              <img
                src={`${process.env.REACT_APP_PRONTUARIO_API}/${signature.url}`}
                alt="signature"
                style={{
                  maxWidth: '400px',
                  maxHeight: '200px',
                  background: 'transparent',
                }}
              />

              <Tooltip title="Remover assinatura">
                <IconButton
                  onClick={() => handleCancelSignature(signature.number)}
                >
                  <Cancel fontSize="large" style={{ color: red[400] }} />
                </IconButton>
              </Tooltip>
            </Typography>

            <Divider className={classes.divider} />

            <Typography variant="subtitle1" component="h3" align="center">
              {signature.name}
            </Typography>

            <Typography variant="subtitle2" component="h3" align="center">
              {`${signature.register}: ${signature.number}`}
            </Typography>
          </div>
        ))}
      </div>

      {alert.isActive && <Snackbar {...alert} />}
    </>
  );
};

export default Signature;
